import { Check } from '@byway/icons';
import clsx from 'clsx';
import { forwardRef, type KeyboardEvent } from 'react';
import { HelpText } from '~/components/HelpText';
import styles from './Checkbox.module.css';
import type { CheckboxProps } from './Checkbox.types';

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { name, id, label, helpText = '', error, errorText = '', checked, disabled, onKeyDown, tabIndex = 0, ...props },
    ref
  ) => {
    const labelStyles = clsx(styles.label, {
      [styles.labelDisabled]: disabled
    });

    const checkboxStyles = clsx(styles.checkbox, {
      [styles.error]: error
    });

    const iconStroke = disabled ? 'var(--by-color-icon-disabled)' : 'var(--by-color-icon-contrast)';

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.key === 'Enter' && !event.defaultPrevented) {
        event.preventDefault();
        event.currentTarget.click();
      }
    };

    const getDescriptors = () => {
      if (errorText) {
        return helpText ? `CheckboxHelpText-${id} CheckboxErrorText-${id}` : `CheckboxErrorText-${id}`;
      }
      return helpText ? `CheckboxHelpText-${id}` : undefined;
    };

    return (
      <div>
        <label htmlFor={id} className={labelStyles}>
          {/* @ts-ignore */}
          <input
            type="checkbox"
            name={name}
            id={id}
            className={styles.input}
            data-test-id={`checkbox-${id}`}
            checked={checked}
            disabled={disabled}
            aria-describedby={getDescriptors()}
            ref={ref}
            aria-invalid={error}
            {...props}
            tabIndex={tabIndex}
            onKeyDown={handleKeyDown}
          />
          <div className={checkboxStyles}>
            <Check decorative size="scale" color={iconStroke} />
          </div>
          {label}
        </label>
        {helpText && (
          <div className={styles.helpText}>
            <HelpText id={`CheckboxHelpText-${id}`} disabled={disabled}>
              {helpText}
            </HelpText>
          </div>
        )}
        {error && errorText ? (
          <div className={styles.errorText}>
            <HelpText id={`CheckboxErrorText-${id}`} disabled={disabled} error={error}>
              {errorText}
            </HelpText>
          </div>
        ) : null}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
